// Paints a view of the clip into an ImageData buffer.
//
// The painter picks a pyramid level with at most about two cells per device
// pixel on each axis, then max-reduces the cells under each pixel, so nothing
// is ever dropped by subsampling. Color comes from a 65536-entry lookup
// table indexed by centi-dB, rebuilt only when the palette or dB range moves.

import { MISSING } from '../model.ts'
import type { HeatMask } from '../overlays/filter.ts'
import type { Clip, View } from './layout.ts'
import { buildPalette, paletteIndex, type PaletteName } from './palette.ts'
import type { Pyramid } from './tiles.ts'

/** Color for a cell with no reading; kept out of every palette's range. */
export const GAP_RGB: [number, number, number] = [0x2a, 0x2a, 0x32]

export class ColorTable {
  readonly lut = new Uint32Array(65536)
  #key = ''

  /** Rebuilds when needed; returns the packed ABGR table for `Uint32Array` pixels. */
  update(palette: PaletteName, dbMin: number, dbMax: number): Uint32Array {
    const key = `${palette}|${dbMin}|${dbMax}`
    if (key === this.#key) return this.lut
    this.#key = key
    const packed = buildPalette(palette)
    const length = packed.length / 3
    const gap = abgr(GAP_RGB[0], GAP_RGB[1], GAP_RGB[2])
    for (let centi = -32768; centi <= 32767; centi++) {
      const slot = centi + 32768
      if (centi === MISSING) {
        this.lut[slot] = gap
        continue
      }
      const index = paletteIndex(centi / 100, dbMin, dbMax, length)
      this.lut[slot] = abgr(packed[index * 3]!, packed[index * 3 + 1]!, packed[index * 3 + 2]!)
    }
    return this.lut
  }
}

function abgr(r: number, g: number, b: number): number {
  return ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0
}

export interface PaintTarget {
  data: Uint32Array
  width: number
  height: number
}

/**
 * Paints `view` into `target`, optionally through a filter mask.
 *
 * The mask is applied to the reduced value rather than to each cell that fed
 * it. Attenuation varies by less than a pixel's worth across the cells under
 * one pixel, and subtracting a constant commutes with max, so the two agree to
 * within the response's slope over a single column.
 */
export function paintView(
  pyramid: Pyramid,
  view: View,
  lut: Uint32Array,
  target: PaintTarget,
  mask?: HeatMask | null,
): void {
  const { width, height, data } = target
  const viewCols = view.col1 - view.col0
  const viewRows = view.row1 - view.row0
  const level = pyramid.choose(viewCols / width, viewRows / height)
  const sx = 1 / 2 ** level.kx
  const sy = 1 / 2 ** level.ky

  // Window edges in level coordinates. The view's columns are run bins, the
  // frame the tape, the overlays and the pointer share; the pyramid's columns
  // start at the clip's first bin, so a crop is translated here. Cells are
  // centered on integers at the base level, so the cell covering base
  // coordinate u is floor(u + 0.5).
  const bin0 = pyramid.clip.bin0
  const lc0 = (view.col0 - bin0 + 0.5) * sx
  const lc1 = (view.col1 - bin0 + 0.5) * sx
  const lr0 = (view.row0 + 0.5) * sy
  const lr1 = (view.row1 + 0.5) * sy
  const colsPerPx = (lc1 - lc0) / width
  const rowsPerPx = (lr1 - lr0) / height

  const rowStart = new Int32Array(height)
  const rowEnd = new Int32Array(height)
  for (let py = 0; py < height; py++) {
    const a = Math.floor(lr0 + py * rowsPerPx)
    let b = Math.floor(lr0 + (py + 1) * rowsPerPx)
    if (b <= a) b = a + 1
    rowStart[py] = Math.max(0, a)
    rowEnd[py] = Math.min(level.rows, b)
  }
  const colStart = new Int32Array(width)
  const colEnd = new Int32Array(width)
  for (let px = 0; px < width; px++) {
    const a = Math.floor(lc0 + px * colsPerPx)
    let b = Math.floor(lc0 + (px + 1) * colsPerPx)
    if (b <= a) b = a + 1
    colStart[px] = Math.max(0, a)
    colEnd[px] = Math.min(level.cols, b)
  }

  const gap = lut[MISSING + 32768]!
  const at = level.at
  for (let py = 0; py < height; py++) {
    const r0 = rowStart[py]!
    const r1 = rowEnd[py]!
    const out = py * width
    if (r1 <= r0) {
      data.fill(gap, out, out + width)
      continue
    }
    for (let px = 0; px < width; px++) {
      const c0 = colStart[px]!
      const c1 = colEnd[px]!
      let best = MISSING
      for (let r = r0; r < r1; r++) {
        for (let c = c0; c < c1; c++) {
          const v = at(r, c)
          if (v !== MISSING && (best === MISSING || v > best)) best = v
        }
      }
      if (mask && best !== MISSING) {
        const floor = mask.floorCenti[px]!
        let cut = best - mask.attenCenti[px]!
        // A filter takes a signal down toward the receiver's own noise and no
        // further, and it never raises a reading that was already under it.
        if (cut < floor) cut = best < floor ? best : floor
        best = cut
      }
      data[out + px] = lut[best + 32768]!
    }
  }
}

/** Convenience for the common case: paint straight into a canvas context. */
export function paintToContext(
  ctx: CanvasRenderingContext2D,
  pyramid: Pyramid,
  view: View,
  lut: Uint32Array,
  width: number,
  height: number,
  mask?: HeatMask | null,
): void {
  const image = ctx.createImageData(width, height)
  paintView(pyramid, view, lut, { data: new Uint32Array(image.data.buffer), width, height }, mask)
  ctx.putImageData(image, 0, 0)
}

export { type Clip }
